import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatToolbar } from '@angular/material/toolbar';
import {
  ButtonsContainerComponent,
  IIntegrationDialogData,
} from '@principle-theorem/ng-shared';
import {
  firebaseFunctionUrl,
  FUNCTIONS_REGION,
  shareReplayCold,
  toTimestamp,
} from '@principle-theorem/shared';
import {
  createSlackLink,
  type ISlackIntegrationData,
  SlackIntegrationStorage,
} from '@principle-theorem/slack-integration';
import { TemporaryOrgTokenStorage } from '@principle-theorem/temporary-tokens';
import * as moment from 'moment-timezone';
import { from, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  type ISlackIntegrationConfig,
  NG_SLACK_INTEGRATION_CONFIG,
} from '../config';

@Component({
  selector: 'pt-manage-slack-integration',
  templateUrl: './manage-slack-integration.component.html',
  styleUrls: ['./manage-slack-integration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButton,
    MatToolbar,
    MatDialogModule,
    ButtonsContainerComponent,
  ],
})
export class ManageSlackIntegrationComponent {
  private _data =
    inject<IIntegrationDialogData<ISlackIntegrationData>>(MAT_DIALOG_DATA);
  slackStorage: SlackIntegrationStorage = new SlackIntegrationStorage();
  tokenStorage: TemporaryOrgTokenStorage = new TemporaryOrgTokenStorage();
  slackLink$: Observable<string>;
  enabled$: Observable<boolean>;

  constructor(
    @Inject(NG_SLACK_INTEGRATION_CONFIG) config: ISlackIntegrationConfig
  ) {
    const scopes: string[] = ['incoming-webhook', 'bot'];
    const oauthApiUrl: string = firebaseFunctionUrl(
      FUNCTIONS_REGION,
      config.projectId,
      'http-slack-oauth'
    );

    this.slackLink$ = from(
      this.tokenStorage.generate(
        {
          orgUid: this._data.orgUid,
          expiresAt: toTimestamp(moment().add('10', 'minutes')),
          deleteAfter: toTimestamp(moment().add('1', 'hour')),
          deleted: false,
        },
        this._data.temporaryTokensCol
      )
    ).pipe(
      map((temporaryToken) =>
        createSlackLink({
          scopes,
          oauthApiUrl,
          clientId: config.slack.clientId,
          temporaryTokenUid: temporaryToken.tokenUid,
        })
      ),
      shareReplayCold()
    );

    this.enabled$ = this.slackStorage.has$(this._data.integrationsCol);
  }

  async removeIntegration(): Promise<void> {
    await this.slackStorage.remove(this._data.integrationsCol);
  }
}
